import { ExperimentConfig } from '@amplitude/experiment-react-native-client';

/** The Experiment SDK's public transport contract (`config.httpClient`). */
type ExperimentHttpClient = NonNullable<ExperimentConfig['httpClient']>;
/** The `{ status, body }` shape the transport must resolve with. */
type SimpleResponse = Awaited<ReturnType<ExperimentHttpClient['request']>>;

/** Outcome of a variants (assignment) request the confirming transport observed. */
export type VariantsFetchOutcome = 'success' | 'failure';
/** Outcome relative to an observation token, including when no new request was observed. */
export type VariantsFetchOutcomeSinceToken = VariantsFetchOutcome | 'none';

// The evaluation (variants) endpoint path; flag requests use a different path and are ignored so
// only assignment requests drive confirmation.
const VARIANTS_REQUEST_PATH = '/sdk/v2/vardata';
const HTTP_STATUS_OK = 200;

// Monotonic counter incremented once per observed variants request, plus the latest outcome. The
// provider serializes its fetches and the SDK issues no background/flags requests, so the latest
// outcome after a token corresponds to that fetch.
let variantsFetchSequence = 0;
let latestVariantsFetchOutcome: VariantsFetchOutcome | undefined;

const recordVariantsFetchOutcome = (outcome: VariantsFetchOutcome): void => {
  variantsFetchSequence += 1;
  latestVariantsFetchOutcome = outcome;
};

/**
 * Rejects after `timeoutMillis`, mirroring the SDK's default transport (which does not abort the
 * underlying request). A non-positive or missing timeout waits indefinitely.
 */
const withRequestTimeout = (
  request: Promise<SimpleResponse>,
  timeoutMillis?: number
): Promise<SimpleResponse> => {
  if (timeoutMillis == null || timeoutMillis <= 0) {
    return request;
  }
  return new Promise<SimpleResponse>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Request timeout after ${timeoutMillis} milliseconds`)),
      timeoutMillis
    );
    request.then(
      response => {
        clearTimeout(timer);
        resolve(response);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
};

/**
 * Amplitude Experiment transport that mirrors the SDK's default fetch/timeout behavior while
 * recording the outcome of each variants request. `ExperimentClient.fetch()` swallows request and
 * timeout errors and always resolves, so the provider cannot otherwise distinguish a failed refresh
 * from an empty assignment; this transport exposes the observed outcome so the provider can retry,
 * keep exposure suppressed, and republish a recovered assignment.
 */
export const confirmingHttpClient: ExperimentHttpClient = {
  async request(requestUrl, method, headers, data, timeoutMillis) {
    const isVariantsRequest = requestUrl.includes(VARIANTS_REQUEST_PATH);
    try {
      const response = await withRequestTimeout(
        (async (): Promise<SimpleResponse> => {
          const httpResponse = await fetch(requestUrl, { method, headers, body: data });
          return { status: httpResponse.status, body: await httpResponse.text() };
        })(),
        timeoutMillis
      );
      if (isVariantsRequest) {
        recordVariantsFetchOutcome(response.status === HTTP_STATUS_OK ? 'success' : 'failure');
      }
      return response;
    } catch (error) {
      if (isVariantsRequest) {
        recordVariantsFetchOutcome('failure');
      }
      throw error;
    }
  },
};

/** Captures a token identifying the latest observed variants request, to compare against later. */
export const markVariantsFetchObservation = (): number => variantsFetchSequence;

/**
 * Returns the outcome of a variants request observed after `token`. Returns `'none'` when no new
 * variants request was observed, e.g. the SDK resolved `fetch()` without issuing a request.
 */
export const getVariantsFetchOutcomeSince = (token: number): VariantsFetchOutcomeSinceToken =>
  variantsFetchSequence > token ? latestVariantsFetchOutcome ?? 'failure' : 'none';
